import React, { useState, useEffect } from 'react';
import { FileDown, Filter, Calendar, TrendingUp, TrendingDown, Target, DollarSign, BarChart3, PieChart, Activity, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const UserReports = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isPrivacyMode, setIsPrivacyMode] = useState(true);
  
  // Obter valor da unidade do usuário (padrão 100 se não configurado)
  const getUserUnitValue = () => {
    if (user?.unit_value && parseFloat(user.unit_value) > 0) {
      return parseFloat(user.unit_value);
    }
    return 100; // Valor padrão se não configurado
  };
  
  const [stats, setStats] = useState({
    total_bets: 0,
    green_bets: 0,
    red_bets: 0,
    void_bets: 0,
    cashout_bets: 0,
    total_volume: 0,
    total_profit: 0,
    win_rate: 0,
    roi: 0
  });
  const [bankrollStats, setBankrollStats] = useState({
    totalInitial: 0,
    totalDeposits: 0,
    totalInvested: 0,
    totalCurrent: 0,
    totalProfit: 0,
    roi: 0
  });
  const [expenses, setExpenses] = useState(0);
  const [allExpenses, setAllExpenses] = useState([]); // Todas as despesas carregadas
  const [withdrawals, setWithdrawals] = useState(0);
  const [allWithdrawals, setAllWithdrawals] = useState([]); // Todos os saques carregados
  const [allDeposits, setAllDeposits] = useState([]); // Todos os depósitos de banca
  const [bets, setBets] = useState([]);
  const [filteredBets, setFilteredBets] = useState([]);
  const [groups, setGroups] = useState([]);
  const [bettingHouses, setBettingHouses] = useState([]);
  const [quickFilter, setQuickFilter] = useState('all');
  const [advancedFilter, setAdvancedFilter] = useState({
    period: 'all',
    customStart: '',
    customEnd: ''
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // Paginação para lista de apostas
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 20;

  useEffect(() => {
    if (user) {
      loadReportsData();
    }
  }, [user]);

  useEffect(() => {
    applyFilters();
  }, [quickFilter, advancedFilter, bets, allExpenses, allWithdrawals]);

  const loadReportsData = async () => {
    try {
      setLoading(true);
      
      // Load user stats
      const statsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stats?user_id=${user.id}`);
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }

      // Load all bets, groups, and betting houses
      const [betsResponse, groupsResponse, housesResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bets?user_id=${user.id}`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/groups?user_id=${user.id}`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/betting-houses?user_id=${user.id}`)
      ]);

      if (betsResponse.ok) {
        const betsData = await betsResponse.json();
        setBets(betsData);
      }

      if (groupsResponse.ok) {
        const groupsData = await groupsResponse.json();
        setGroups(groupsData);
      }

      if (housesResponse.ok) {
        const housesData = await housesResponse.json();
        setBettingHouses(housesData);
        
        // Calcular estatísticas da gestão de banca
        const totalInitial = housesData.reduce((sum, h) => {
          const initial = h.initial_bankroll - (h.additional_deposits || 0);
          return sum + initial;
        }, 0);
        
        const totalDeposits = housesData.reduce((sum, h) => sum + (h.additional_deposits || 0), 0);
        // CORREÇÃO CRÍTICA: total investido = initial_bankroll (não somar additional_deposits novamente!)
        const totalInvested = housesData.reduce((sum, h) => sum + (h.initial_bankroll || 0), 0);
        
        // CORREÇÃO CRÍTICA: Calcular banca atual considerando current_bankroll
        // que já está sendo atualizado automaticamente pelo backend
        const totalCurrent = housesData.reduce((sum, h) => sum + (h.current_bankroll || 0), 0);
        
        const totalProfit = totalCurrent - totalInvested;
        const bankrollROI = totalInvested > 0 ? (totalProfit / totalInvested * 100) : 0;
        
        console.log('📊 UserReports - Bankroll Stats:');
        console.log('  Houses:', housesData.length);
        console.log('  Total Initial:', totalInitial);
        console.log('  Total Deposits:', totalDeposits);
        console.log('  Total Invested:', totalInvested);
        console.log('  Total Current:', totalCurrent);
        console.log('  Total Profit:', totalProfit);
        console.log('  ROI:', bankrollROI);
        
        setBankrollStats({
          totalInitial,
          totalDeposits,
          totalInvested,
          totalCurrent,
          totalProfit,
          roi: bankrollROI
        });
      }

      // Load ALL expenses (sem filtro de data aqui)
      const expensesResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/expenses?user_id=${user.id}`);
      if (expensesResponse.ok) {
        const expensesData = await expensesResponse.json();
        setAllExpenses(expensesData); // Armazenar TODAS as despesas
      }

      // Load ALL withdrawals (sem filtro de data aqui)
      const withdrawalsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/withdrawals?user_id=${user.id}`);
      if (withdrawalsResponse.ok) {
        const withdrawalsData = await withdrawalsResponse.json();
        setAllWithdrawals(withdrawalsData); // Armazenar TODOS os saques
      }

      // 🆕 Load ALL bankroll deposits (depósitos de banca)
      const depositsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bankroll-deposits?user_id=${user.id}`);
      if (depositsResponse.ok) {
        const depositsData = await depositsResponse.json();
        setAllDeposits(depositsData); // Armazenar TODOS os depósitos de banca
      }
      
    } catch (error) {
      console.error('Error loading reports data:', error);
    } finally {
      setLoading(false);
    }
  };

  // FUNÇÃO CORRIGIDA PARA CALCULAR BANCA INICIAL E FINAL DE UM PERÍODO
  const calculateBankrollForPeriod = (startDate, endDate) => {
    console.log('🔍 calculateBankrollForPeriod chamada:');
    console.log('  startDate:', startDate);
    console.log('  endDate:', endDate);
    
    // Calcular banca inicial do período (saldo que existia ANTES do período começar)
    let bankrollInicial = 0;
    
    if (!startDate) {
      // Se não há filtro de data, "Banca Inicial" é o capital original investido
      bankrollInicial = bankrollStats.totalInvested;
    } else {
      // Calcular o saldo que existia no INÍCIO do período filtrado
      bettingHouses.forEach(house => {
        // 1. Depósitos feitos ANTES do início do período
        const depositsBeforeStart = allDeposits.filter(d => {
          const depositDate = new Date(d.date);
          return d.betting_house_id === house.id && depositDate < startDate;
        });
        const totalDepositsBeforeStart = depositsBeforeStart.reduce((sum, d) => sum + d.amount, 0);
        
        // 2. Lucro/prejuízo acumulado ANTES do início do período
        const betsBeforeStart = bets.filter(bet => {
          const betDate = new Date(bet.date);
          return bet.betting_house_id === house.id && 
                 bet.status === 'completed' &&
                 betDate < startDate;
        });
        const profitBeforeStart = betsBeforeStart.reduce((sum, bet) => sum + (bet.profit || 0), 0);
        
        // 3. Saques feitos ANTES do início do período
        const withdrawalsBeforeStart = allWithdrawals.filter(w => {
          const withdrawalDate = new Date(w.date);
          return w.betting_house_id === house.id && withdrawalDate < startDate;
        });
        const totalWithdrawalsBeforeStart = withdrawalsBeforeStart.reduce((sum, w) => sum + w.amount, 0);
        
        // Banca Inicial = Depósitos - Saques + Lucro (tudo antes do período)
        bankrollInicial += totalDepositsBeforeStart + profitBeforeStart - totalWithdrawalsBeforeStart;
      });
    }
    
    // CORREÇÃO: Calcular Banca Final no FIM do período (não o saldo atual)
    let bankrollFinal = 0;
    
    if (!endDate) {
      // Se não há endDate, usar o saldo atual
      bankrollFinal = bankrollStats.totalCurrent;
    } else {
      // Calcular o saldo que existia ATÉ O FIM do período filtrado
      bettingHouses.forEach(house => {
        // 1. Depósitos feitos ATÉ o fim do período
        const depositsUntilEnd = allDeposits.filter(d => {
          const depositDate = new Date(d.date);
          return d.betting_house_id === house.id && depositDate <= endDate;
        });
        const totalDepositsUntilEnd = depositsUntilEnd.reduce((sum, d) => sum + d.amount, 0);
        
        // 2. Lucro/prejuízo acumulado ATÉ o fim do período
        const betsUntilEnd = bets.filter(bet => {
          const betDate = new Date(bet.date);
          return bet.betting_house_id === house.id && 
                 bet.status === 'completed' &&
                 betDate <= endDate;
        });
        const profitUntilEnd = betsUntilEnd.reduce((sum, bet) => sum + (bet.profit || 0), 0);
        
        // 3. Saques feitos ATÉ o fim do período
        const withdrawalsUntilEnd = allWithdrawals.filter(w => {
          const withdrawalDate = new Date(w.date);
          return w.betting_house_id === house.id && withdrawalDate <= endDate;
        });
        const totalWithdrawalsUntilEnd = withdrawalsUntilEnd.reduce((sum, w) => sum + w.amount, 0);
        
        // Banca Final = Depósitos - Saques + Lucro (tudo ATÉ o fim do período)
        bankrollFinal += totalDepositsUntilEnd + profitUntilEnd - totalWithdrawalsUntilEnd;
      });
    }
    
    // Calcular depósitos feitos DURANTE o período
    const depositsInPeriod = allDeposits.filter(d => {
      const depositDate = new Date(d.date);
      return (!startDate || depositDate >= startDate) && (!endDate || depositDate <= endDate);
    });
    const depositosDoPeriodo = depositsInPeriod.reduce((sum, d) => sum + d.amount, 0);
    
    // Calcular saques feitos DURANTE o período
    const withdrawalsInPeriod = allWithdrawals.filter(w => {
      const withdrawalDate = new Date(w.date);
      return (!startDate || withdrawalDate >= startDate) && (!endDate || withdrawalDate <= endDate);
    });
    const saquesDoPeriodo = withdrawalsInPeriod.reduce((sum, w) => sum + w.amount, 0);
    
    // Lucro do período = (Banca Final - Banca Inicial) - Depósitos do Período + Saques do Período
    const lucroDoPeriodo = (bankrollFinal - bankrollInicial) - depositosDoPeriodo + saquesDoPeriodo;
    
    console.log('📊 Cálculo CORRIGIDO de Banca do Período:');
    console.log('  Banca Inicial (início do período):', bankrollInicial);
    console.log('  Banca Final (saldo atual SEMPRE):', bankrollFinal);
    console.log('  Depósitos do Período:', depositosDoPeriodo);
    console.log('  Saques do Período:', saquesDoPeriodo);
    console.log('  Lucro do Período:', lucroDoPeriodo);
    
    return {
      bankrollInicial,
      bankrollFinal,
      depositosDoPeriodo,
      saquesDoPeriodo,
      lucroDoPeriodo
    };
  };

  const applyFilters = () => {
    let filtered = [...bets];
    const now = new Date();
    
    // Manter timezone local (BRT) mas garantir início/fim correto do dia
    // Criar data base às 00:00:00 do dia atual no timezone local
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    
    let startDate = null;
    let endDate = null;

    // Apply quick filters
    if (quickFilter === '24h') {
      // HOJE - Do início até o fim do dia atual
      startDate = new Date(today);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(today);
      endDate.setHours(23, 59, 59, 999);
    } else if (quickFilter === 'yesterday') {
      // ONTEM - Dia completo anterior
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      startDate = new Date(yesterday);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(yesterday);
      endDate.setHours(23, 59, 59, 999);
    } else if (quickFilter === '3days') {
      // ÚLTIMOS 3 DIAS - Inclui hoje
      const threeDaysAgo = new Date(today);
      threeDaysAgo.setDate(threeDaysAgo.getDate() - 2);
      startDate = new Date(threeDaysAgo);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(today);
      endDate.setHours(23, 59, 59, 999);
    } else if (quickFilter === 'all') {
      // Apply advanced filters
      if (advancedFilter.period === '7days') {
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
        startDate = new Date(sevenDaysAgo);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(today);
        endDate.setHours(23, 59, 59, 999);
      } else if (advancedFilter.period === 'month') {
        // Primeiro dia do mês atual às 00:00:00
        startDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
        // Último dia do mês atual às 23:59:59.999
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
      } else if (advancedFilter.period === 'quarter') {
        const quarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(now.getFullYear(), quarter * 3, 1, 0, 0, 0, 0);
        endDate = new Date(now.getFullYear(), (quarter + 1) * 3, 0, 23, 59, 59, 999);
      } else if (advancedFilter.period === 'year') {
        startDate = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
        endDate = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
      } else if (advancedFilter.period === 'custom' && advancedFilter.customStart) {
        const startParts = advancedFilter.customStart.split('-');
        startDate = new Date(parseInt(startParts[0]), parseInt(startParts[1]) - 1, parseInt(startParts[2]), 0, 0, 0, 0);
        if (advancedFilter.customEnd) {
          const endParts = advancedFilter.customEnd.split('-');
          endDate = new Date(parseInt(endParts[0]), parseInt(endParts[1]) - 1, parseInt(endParts[2]), 23, 59, 59, 999);
        }
      }
    }

    // Filter by date range
    if (startDate && endDate) {
      filtered = filtered.filter(bet => {
        const betDate = new Date(bet.date);
        return betDate >= startDate && betDate <= endDate;
      });
    } else if (startDate) {
      filtered = filtered.filter(bet => {
        const betDate = new Date(bet.date);
        return betDate >= startDate;
      });
    }

    // 🔧 CORREÇÃO: Filtrar despesas pelo mesmo período das apostas
    let filteredExpenses = allExpenses;
    if (startDate && endDate) {
      filteredExpenses = allExpenses.filter(expense => {
        const expenseDate = new Date(expense.date);
        return expenseDate >= startDate && expenseDate <= endDate;
      });
    } else if (startDate) {
      filteredExpenses = allExpenses.filter(expense => {
        const expenseDate = new Date(expense.date);
        return expenseDate >= startDate;
      });
    }
    
    // Calcular total de despesas filtradas
    const totalFilteredExpenses = filteredExpenses.reduce((sum, expense) => sum + (expense.amount || 0), 0);
    setExpenses(totalFilteredExpenses);

    // 🔧 Filtrar saques pelo mesmo período das apostas
    let filteredWithdrawals = allWithdrawals;
    if (startDate && endDate) {
      filteredWithdrawals = allWithdrawals.filter(withdrawal => {
        const withdrawalDate = new Date(withdrawal.date);
        return withdrawalDate >= startDate && withdrawalDate <= endDate;
      });
    } else if (startDate) {
      filteredWithdrawals = allWithdrawals.filter(withdrawal => {
        const withdrawalDate = new Date(withdrawal.date);
        return withdrawalDate >= startDate;
      });
    }
    
    // Calcular total de saques filtrados
    const totalFilteredWithdrawals = filteredWithdrawals.reduce((sum, w) => sum + (w.amount || 0), 0);
    setWithdrawals(totalFilteredWithdrawals);

    setFilteredBets(filtered);
  };

  const calculateFilteredStats = () => {
    // Filtrar apenas apostas finalizadas (não pendentes) para o cálculo de estatísticas
    const completedBets = filteredBets.filter(bet => bet.status !== 'pending');
    
    const totalBets = completedBets.length;
    const greenBets = completedBets.filter(bet => bet.result === 'green').length;
    const redBets = completedBets.filter(bet => bet.result === 'red').length;
    const voidBets = completedBets.filter(bet => bet.result === 'void').length;
    const cashoutBets = completedBets.filter(bet => bet.result === 'cashout').length;
    
    const totalVolume = completedBets.reduce((sum, bet) => sum + (bet.stake || 0), 0);
    const totalProfit = completedBets.reduce((sum, bet) => sum + (bet.profit || 0), 0);
    const winRate = totalBets > 0 ? (greenBets / totalBets) * 100 : 0;
    const roi = totalVolume > 0 ? (totalProfit / totalVolume) * 100 : 0;
    const finalResult = totalProfit - (expenses || 0);

    // Contar apostas pendentes separadamente
    const pendingBets = filteredBets.filter(bet => bet.status === 'pending').length;

    return {
      totalBets,
      greenBets,
      redBets,
      voidBets,
      cashoutBets,
      pendingBets,
      totalVolume,
      totalProfit,
      winRate,
      roi,
      finalResult
    };
  };

  const filteredStats = calculateFilteredStats();

  const formatCurrency = (value) => {
    if (isPrivacyMode) return 'R$ •••••';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatPercentage = (value) => {
    if (isPrivacyMode) return '••••%';
    return value.toFixed(2) + '%';
  };

  const getGroupName = (groupId) => {
    if (!groupId) return 'Sem grupo';
    const group = groups.find(g => g.id === groupId);
    return group ? group.name : 'Grupo não encontrado';
  };

  const getBettingHouseName = (houseId) => {
    const house = bettingHouses.find(h => h.id === houseId);
    return house ? house.name : 'Casa não encontrada';
  };

  const getBetType = (bet) => {
    if (bet.comments && bet.comments.includes('Registro diário')) {
      return 'Diário';
    }
    return 'Individual';
  };

  const exportToCSV = () => {
    if (filteredBets.length === 0) {
      alert('Nenhuma aposta para exportar no período selecionado.');
      return;
    }

    const headers = ['Data e Hora', 'Casa de Apostas', 'Grupo', 'Tipo', 'Odd', 'Stake', 'Resultado', 'Lucro', 'Comentários'];
    const csvData = filteredBets.map(bet => [
      `${new Date(bet.date).toLocaleDateString('pt-BR', { timeZone: 'UTC' })} ${new Date(bet.date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })}`,
      getBettingHouseName(bet.betting_house_id),
      getGroupName(bet.group_id),
      getBetType(bet),
      bet.odd,
      bet.stake,
      bet.result,
      bet.profit,
      bet.comments || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `relatório-apostas-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getPeriodText = () => {
    if (quickFilter !== 'all') {
      switch (quickFilter) {
        case '24h': return 'Hoje';
        case 'yesterday': return 'Ontem';
        case '3days': return 'Últimos 3 dias';
        default: return 'Todos os períodos';
      }
    }

    switch (advancedFilter.period) {
      case '7days': return 'Últimos 7 dias';
      case 'month': return 'Este mês';
      case 'quarter': return 'Este trimestre';
      case 'year': return 'Este ano';
      case 'custom': 
        if (advancedFilter.customStart && advancedFilter.customEnd) {
          return `${new Date(advancedFilter.customStart).toLocaleDateString('pt-BR')} - ${new Date(advancedFilter.customEnd).toLocaleDateString('pt-BR')}`;
        }
        return 'Período personalizado';
      default: return 'Todos os períodos';
    }
  };

  const handleRecalculateBankrolls = async () => {
    if (!confirm('🔧 Recalcular valores da banca?\n\nIsso irá corrigir os valores baseado no histórico de apostas.\n\nDeseja continuar?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/betting-houses/recalculate-bankrolls?user_id=${user.id}`, {
        method: 'POST'
      });

      if (response.ok) {
        const data = await response.json();
        alert(`✅ Sucesso!\n\n${data.message}\n\nOs valores foram recalculados. Recarregando dados...`);
        // Recarregar dados
        window.location.reload();
      } else {
        throw new Error('Erro ao recalcular');
      }
    } catch (error) {
      console.error('Error recalculating bankrolls:', error);
      alert('❌ Erro ao recalcular valores. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Cálculos de paginação para lista de apostas
  const totalPages = Math.ceil(filteredBets.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedBets = filteredBets.slice(startIndex, endIndex);

  // Resetar para página 1 quando filtros mudarem
  useEffect(() => {
    setCurrentPage(1);
  }, [quickFilter, advancedFilter]);

  const goToPage = (page) => {
    setCurrentPage(page);
    // Scroll suave para a seção de lista de apostas
    const listElement = document.getElementById('bets-list-section');
    if (listElement) {
      listElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="text-gray-500 ml-4">Carregando relatórios...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-2 sm:p-0">
      {/* Header - Mobile Optimized */}
      <div className="flex flex-col space-y-3 sm:space-y-4">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
            Relatórios
          </h1>
          <p className="text-xs sm:text-sm md:text-base text-gray-600 dark:text-gray-400 mt-1">
            Análise detalhada da sua performance
          </p>
        </div>
        <div className="grid grid-cols-1 sm:flex sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
          <button
            onClick={() => setIsPrivacyMode(!isPrivacyMode)}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-3 sm:py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            title={isPrivacyMode ? 'Mostrar valores' : 'Ocultar valores'}
          >
            {isPrivacyMode ? (
              <Eye className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            ) : (
              <EyeOff className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            )}
            <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">
              {isPrivacyMode ? 'Mostrar Valores' : 'Ocultar Valores'}
            </span>
          </button>
          <button
            onClick={handleRecalculateBankrolls}
            disabled={loading}
            className="w-full sm:w-auto bg-gradient-to-r from-orange-600 to-red-600 text-white px-4 py-3 sm:py-2 rounded-lg hover:from-orange-700 hover:to-red-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 font-medium"
            title="Recalcular valores da banca"
          >
            <Activity className="w-4 h-4" />
            <span className="text-sm">{loading ? 'Recalculando...' : '🔧 Recalcular'}</span>
          </button>
          <button
            onClick={exportToCSV}
            className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-3 sm:py-2 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors flex items-center justify-center gap-2 font-medium"
          >
            <FileDown className="w-4 h-4" />
            <span className="text-sm">Exportar CSV</span>
          </button>
        </div>
      </div>

      {/* Quick Filters - Mobile Optimized */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-3 sm:p-4 md:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">Filtros Rápidos</h3>
        <div className="flex flex-wrap gap-2 mb-3 sm:mb-4">
          {[
            { id: 'all', label: 'Todos' },
            { id: '24h', label: 'Hoje' },
            { id: 'yesterday', label: 'Ontem' },
            { id: '3days', label: '3 dias' }
          ].map(filter => (
            <button
              key={filter.id}
              onClick={() => setQuickFilter(filter.id)}
              className={`px-3 sm:px-4 py-2 sm:py-2 text-xs sm:text-sm rounded-lg border transition-colors ${
                quickFilter === filter.id
                  ? 'bg-blue-500 text-white border-blue-500'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {filter.label}
            </button>
          ))}
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="px-3 sm:px-4 py-2 sm:py-2 text-xs sm:text-sm rounded-lg border border-purple-300 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors flex items-center space-x-1.5 sm:space-x-2"
          >
            <Filter className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Filtros Avançados</span>
            <span className="sm:hidden">Avançado</span>
          </button>
        </div>

        {/* Advanced Filters */}
        {showAdvancedFilters && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Filtros Avançados de Período</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Período</label>
                <select
                  value={advancedFilter.period}
                  onChange={(e) => setAdvancedFilter(prev => ({ ...prev, period: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="all">Todos os períodos</option>
                  <option value="7days">Últimos 7 dias</option>
                  <option value="month">Este mês</option>
                  <option value="quarter">Este trimestre</option>
                  <option value="year">Este ano</option>
                  <option value="custom">Período personalizado</option>
                </select>
              </div>
              
              {advancedFilter.period === 'custom' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Data Início</label>
                    <input
                      type="date"
                      value={advancedFilter.customStart}
                      onChange={(e) => setAdvancedFilter(prev => ({ ...prev, customStart: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Data Fim</label>
                    <input
                      type="date"
                      value={advancedFilter.customEnd}
                      onChange={(e) => setAdvancedFilter(prev => ({ ...prev, customEnd: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Período selecionado: <span className="font-semibold">{getPeriodText()}</span> ({filteredStats.totalBets} apostas)
            </p>
          </div>
        )}
      </div>

      {/* Main Stats Cards - Mobile Optimized */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
        {/* Lucro das Apostas */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 md:p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-2 sm:mb-3 md:mb-4">
            <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
            </div>
          </div>
          <h3 className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm font-medium mb-1">Lucro Apostas</h3>
          <p className={`text-base sm:text-xl md:text-2xl font-bold ${filteredStats.totalProfit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {formatCurrency(filteredStats.totalProfit)}
          </p>
          <p className="text-xs text-gray-500 hidden sm:block">Apenas apostas</p>
        </div>

        {/* Taxa de Acerto */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 md:p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-2 sm:mb-3 md:mb-4">
            <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center">
              <Target className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
            </div>
          </div>
          <h3 className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm font-medium mb-1">Taxa Acerto</h3>
          <p className="text-base sm:text-xl md:text-2xl font-bold text-blue-600 dark:text-blue-400">
            {formatPercentage(filteredStats.winRate)}
          </p>
        </div>

        {/* Despesas (30 dias) */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 md:p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-2 sm:mb-3 md:mb-4">
            <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-r from-red-500 to-pink-600 rounded-lg flex items-center justify-center">
              <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
            </div>
          </div>
          <h3 className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm font-medium mb-1">Despesas</h3>
          <p className="text-base sm:text-xl md:text-2xl font-bold text-red-600 dark:text-red-400">
            -{formatCurrency(expenses)}
          </p>
          <p className="text-xs text-gray-500 hidden sm:block">Grupos e ferramentas</p>
        </div>

        {/* Saques */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 md:p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-2 sm:mb-3 md:mb-4">
            <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
              <TrendingDown className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
            </div>
          </div>
          <h3 className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm font-medium mb-1">Total Sacado</h3>
          <p className="text-base sm:text-xl md:text-2xl font-bold text-cyan-600 dark:text-cyan-400">
            {formatCurrency(withdrawals)}
          </p>
          <p className="text-xs text-gray-500 hidden sm:block">Valores retirados</p>
        </div>

        {/* Resultado Final */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 md:p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-2 sm:mb-3 md:mb-4">
            <div className={`w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center ${filteredStats.finalResult >= 0 ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-gradient-to-r from-red-500 to-pink-600'}`}>
              <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
            </div>
          </div>
          <h3 className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm font-medium mb-1">Resultado Final</h3>
          <p className={`text-base sm:text-xl md:text-2xl font-bold ${filteredStats.finalResult >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {formatCurrency(filteredStats.finalResult)}
          </p>
          <p className="text-xs text-gray-500 hidden sm:block">Apostas - Despesas</p>
        </div>

        {/* Volume Total */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 md:p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-2 sm:mb-3 md:mb-4">
            <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
              <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
            </div>
          </div>
          <h3 className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm font-medium mb-1">Volume Total</h3>
          <p className="text-base sm:text-xl md:text-2xl font-bold text-purple-600 dark:text-purple-400">
            {formatCurrency(filteredStats.totalVolume)}
          </p>
          <p className="text-xs text-gray-500 hidden sm:block">{isPrivacyMode ? '••' : filteredStats.totalBets} apostas</p>
        </div>

        {/* ROI Final */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 md:p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-2 sm:mb-3 md:mb-4">
            <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-r from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
              <Activity className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
            </div>
          </div>
          <h3 className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm font-medium mb-1">ROI Final</h3>
          <p className={`text-base sm:text-xl md:text-2xl font-bold ${filteredStats.roi >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {formatPercentage(filteredStats.roi)}
          </p>
          <p className="text-xs text-gray-500 hidden sm:block">Com despesas</p>
        </div>
        
        {/* Resultado da Gestão de Banca - Mobile Optimized */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 md:p-6 shadow-lg border border-gray-100 dark:border-gray-700 col-span-2 lg:col-span-3">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Target className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
              </div>
              <div>
                <h3 className="text-gray-900 dark:text-white text-sm sm:text-base md:text-lg font-semibold">Gestão de Banca</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">Evolução da banca no período selecionado</p>
              </div>
            </div>
          </div>
          
          {(() => {
            // Calcular banca do período baseado nos filtros atuais
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            let startDate = null;
            let endDate = null;
            
            if (quickFilter === '24h') {
              startDate = new Date(today);
              endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
            } else if (quickFilter === 'yesterday') {
              const yesterday = new Date(today);
              yesterday.setDate(yesterday.getDate() - 1);
              startDate = new Date(yesterday);
              endDate = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59, 999);
            } else if (quickFilter === '3days') {
              const threeDaysAgo = new Date(today);
              threeDaysAgo.setDate(threeDaysAgo.getDate() - 2);
              startDate = new Date(threeDaysAgo);
              endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
            } else if (quickFilter === 'all') {
              if (advancedFilter.period === '7days') {
                const sevenDaysAgo = new Date(today);
                sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
                startDate = new Date(sevenDaysAgo);
                endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
              } else if (advancedFilter.period === 'month') {
                startDate = new Date(now.getFullYear(), now.getMonth(), 1);
                endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
              } else if (advancedFilter.period === 'quarter') {
                const quarter = Math.floor(now.getMonth() / 3);
                startDate = new Date(now.getFullYear(), quarter * 3, 1);
                endDate = new Date(now.getFullYear(), (quarter + 1) * 3, 0, 23, 59, 59, 999);
              } else if (advancedFilter.period === 'year') {
                startDate = new Date(now.getFullYear(), 0, 1);
                endDate = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
              } else if (advancedFilter.period === 'custom' && advancedFilter.customStart) {
                const startParts = advancedFilter.customStart.split('-');
                startDate = new Date(parseInt(startParts[0]), parseInt(startParts[1]) - 1, parseInt(startParts[2]));
                if (advancedFilter.customEnd) {
                  const endParts = advancedFilter.customEnd.split('-');
                  endDate = new Date(parseInt(endParts[0]), parseInt(endParts[1]) - 1, parseInt(endParts[2]), 23, 59, 59, 999);
                }
              }
            }
            
            const periodStats = calculateBankrollForPeriod(startDate, endDate);
            const roiPeriod = periodStats.bankrollInicial > 0 ? (periodStats.lucroDoPeriodo / periodStats.bankrollInicial * 100) : 0;
            
            return (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-3 md:gap-4">
                  {/* Banca Inicial do Período */}
                  <div className="text-center p-2 sm:p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1">Banca Inicial</p>
                    <p className="text-sm sm:text-base md:text-lg font-bold text-gray-900 dark:text-white">
                      {formatCurrency(periodStats.bankrollInicial)}
                    </p>
                  </div>
                  
                  {/* Depósitos do Período */}
                  {periodStats.depositosDoPeriodo > 0 && (
                    <div className="text-center p-2 sm:p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1">Depósitos +</p>
                      <p className="text-sm sm:text-base md:text-lg font-bold text-green-600 dark:text-green-400">
                        +{formatCurrency(periodStats.depositosDoPeriodo)}
                      </p>
                    </div>
                  )}
                  
                  {/* Saques do Período */}
                  {periodStats.saquesDoPeriodo > 0 && (
                    <div className="text-center p-2 sm:p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1">Saques -</p>
                      <p className="text-sm sm:text-base md:text-lg font-bold text-red-600 dark:text-red-400">
                        -{formatCurrency(periodStats.saquesDoPeriodo)}
                      </p>
                    </div>
                  )}
                  
                  {/* Lucro do Período */}
                  <div className={`text-center p-2 sm:p-3 rounded-lg ${periodStats.lucroDoPeriodo >= 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1">Lucro Período</p>
                    <p className={`text-sm sm:text-base md:text-lg font-bold ${periodStats.lucroDoPeriodo >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {periodStats.lucroDoPeriodo >= 0 ? '+' : ''}{formatCurrency(periodStats.lucroDoPeriodo)}
                    </p>
                  </div>
                  
                  {/* Banca Final do Período */}
                  <div className="text-center p-2 sm:p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1">Banca Final</p>
                    <p className="text-sm sm:text-base md:text-lg font-bold text-purple-600 dark:text-purple-400">
                      {formatCurrency(periodStats.bankrollFinal)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {advancedFilter.period === 'all' && quickFilter === 'all' ? 'Saldo atual' : 'Fim do período'}
                    </p>
                  </div>
                  
                  {/* ROI do Período */}
                  <div className={`text-center p-2 sm:p-3 rounded-lg ${roiPeriod >= 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1">ROI Período</p>
                    <p className={`text-sm sm:text-base md:text-lg font-bold ${roiPeriod >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {roiPeriod >= 0 ? '+' : ''}{roiPeriod.toFixed(1)}%
                    </p>
                  </div>
                </div>
                
                <div className="mt-3 sm:mt-4 p-2 sm:p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    💡 <strong>Como funciona:</strong> A "Banca Inicial" é o saldo no INÍCIO do período. 
                    A "Banca Final" é o saldo no FIM do período selecionado. O "Lucro do Período" já desconta depósitos e saques feitos durante o período.
                  </p>
                </div>
              </>
            );
          })()}
        </div>
      </div>

      {/* Distribution of Results - Mobile Optimized */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700">
        <div className="p-3 sm:p-4 md:p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Distribuição de Resultados</h3>
        </div>
        <div className="p-3 sm:p-4 md:p-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 md:gap-4">
            {[
              { key: 'green', label: 'Green', fullLabel: 'Green (Ganhou)', color: 'text-green-600 dark:text-green-400', count: filteredStats.greenBets },
              { key: 'red', label: 'Red', fullLabel: 'Red (Perdeu)', color: 'text-red-600 dark:text-red-400', count: filteredStats.redBets },
              { key: 'void', label: 'Void', fullLabel: 'Void (Anulada)', color: 'text-gray-600 dark:text-gray-400', count: filteredStats.voidBets },
              { key: 'cashout', label: 'Cashout', fullLabel: 'Cashout (Encerrada)', color: 'text-blue-600 dark:text-blue-400', count: filteredStats.cashoutBets },
              { key: 'pending', label: 'Pendente', fullLabel: 'Pendentes', color: 'text-orange-600 dark:text-orange-400', count: filteredStats.pendingBets }
            ].map(result => (
              <div key={result.key} className="text-center p-2 sm:p-3 md:p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <h4 className="font-medium text-gray-900 dark:text-white text-xs sm:text-sm mb-1 sm:mb-2">
                  <span className="sm:hidden">{result.label}</span>
                  <span className="hidden sm:inline">{result.fullLabel}</span>
                </h4>
                <p className={`text-lg sm:text-xl md:text-2xl font-bold ${result.color}`}>{result.count}</p>
                <p className={`text-xs sm:text-sm ${result.color} mt-1`}>
                  {result.key === 'cashout' ? 'Variável' : 
                   result.key === 'pending' ? 'Aguardando' : 
                   formatCurrency(
                    filteredBets.filter(bet => bet.result === result.key).reduce((sum, bet) => sum + (bet.profit || 0), 0)
                  )}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Performance por Casa de Apostas - Mobile Optimized */}
      {filteredStats.totalBets > 0 && bettingHouses.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="p-3 sm:p-4 md:p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
              🏢 Desempenho por Casa de Apostas
            </h3>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
              Análise detalhada de performance em cada casa cadastrada
            </p>
          </div>
          <div className="p-3 sm:p-4 md:p-6">
            <div className="space-y-3 sm:space-y-4 md:space-y-6">
              {bettingHouses.map(house => {
                // Filtrar apostas desta casa
                const houseBets = filteredBets.filter(bet => bet.betting_house_id === house.id);
                
                if (houseBets.length === 0) return null; // Pula se não tem apostas no período
                
                const totalBets = houseBets.length;
                const greenBets = houseBets.filter(b => b.result === 'green').length;
                const redBets = houseBets.filter(b => b.result === 'red').length;
                const voidBets = houseBets.filter(b => b.result === 'void').length;
                const cashoutBets = houseBets.filter(b => b.result === 'cashout').length;
                const totalVolume = houseBets.reduce((sum, b) => sum + (b.stake || 0), 0);
                const totalProfit = houseBets.reduce((sum, b) => sum + (b.profit || 0), 0);
                const winRate = totalBets > 0 ? (greenBets / totalBets * 100) : 0;
                const roi = totalVolume > 0 ? (totalProfit / totalVolume * 100) : 0;

                return (
                  <div key={house.id} className="border border-gray-200 dark:border-gray-700 rounded-xl p-3 sm:p-4 hover:shadow-md transition-shadow">
                    {/* Header - Mobile Optimized */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-3 sm:mb-4 pb-2 sm:pb-3 border-b border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                          <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                        </div>
                        <div>
                          <h4 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white">{house.name}</h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {totalBets} apostas no período
                          </p>
                        </div>
                      </div>
                      <div className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg self-start sm:self-auto ${totalProfit >= 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Lucro</p>
                        <p className={`text-base sm:text-lg font-bold ${totalProfit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {formatCurrency(totalProfit)}
                        </p>
                      </div>
                    </div>

                    {/* Métricas - Mobile Optimized */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
                      <div className="text-center p-2 sm:p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1">Apostas</p>
                        <p className="text-base sm:text-lg md:text-xl font-bold text-gray-900 dark:text-white">{totalBets}</p>
                      </div>
                      
                      <div className="text-center p-2 sm:p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1">Green</p>
                        <p className="text-base sm:text-lg md:text-xl font-bold text-green-600 dark:text-green-400">{greenBets}</p>
                      </div>
                      
                      <div className="text-center p-2 sm:p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1">Red</p>
                        <p className="text-base sm:text-lg md:text-xl font-bold text-red-600 dark:text-red-400">{redBets}</p>
                      </div>
                      
                      <div className="text-center p-2 sm:p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1">Taxa</p>
                        <p className="text-base sm:text-lg md:text-xl font-bold text-blue-600 dark:text-blue-400">{winRate.toFixed(1)}%</p>
                      </div>
                      
                      <div className={`text-center p-2 sm:p-3 rounded-lg ${roi >= 0 ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1">ROI</p>
                        <p className={`text-base sm:text-lg md:text-xl font-bold ${roi >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {roi >= 0 ? '+' : ''}{roi.toFixed(1)}%
                        </p>
                      </div>
                    </div>

                    {/* Informações Adicionais - Mobile Optimized */}
                    <div className="mt-2 sm:mt-3 grid grid-cols-2 gap-2 sm:gap-3">
                      <div className="bg-purple-50 dark:bg-purple-900/20 p-2 sm:p-3 rounded-lg">
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1">Volume Total</p>
                        <p className="text-xs sm:text-sm font-bold text-purple-600 dark:text-purple-400">{formatCurrency(totalVolume)}</p>
                      </div>
                      
                      {(voidBets > 0 || cashoutBets > 0) && (
                        <div className="bg-gray-50 dark:bg-gray-700 p-2 sm:p-3 rounded-lg">
                          <p className="text-xs text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1">Outras</p>
                          <p className="text-xs sm:text-sm font-bold text-gray-600 dark:text-gray-400">
                            {voidBets > 0 && `${voidBets} void`}
                            {voidBets > 0 && cashoutBets > 0 && ', '}
                            {cashoutBets > 0 && `${cashoutBets} cashout`}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Comparação Rápida - Mobile Optimized */}
            <div className="mt-3 sm:mt-4 md:mt-6 p-3 sm:p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <h4 className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white mb-1.5 sm:mb-2">💡 Insights Rápidos:</h4>
              <div className="grid grid-cols-1 gap-1.5 sm:gap-2 text-xs text-gray-600 dark:text-gray-400">
                {(() => {
                  const housesWithProfit = bettingHouses.map(house => {
                    const houseBets = filteredBets.filter(bet => bet.betting_house_id === house.id);
                    const profit = houseBets.reduce((sum, b) => sum + (b.profit || 0), 0);
                    return { name: house.name, profit, bets: houseBets.length };
                  }).filter(h => h.bets > 0);

                  const bestHouse = housesWithProfit.sort((a, b) => b.profit - a.profit)[0];
                  const worstHouse = housesWithProfit.sort((a, b) => a.profit - b.profit)[0];

                  return (
                    <>
                      {bestHouse && (
                        <p>🏆 <strong>Melhor casa:</strong> {bestHouse.name} ({formatCurrency(bestHouse.profit)})</p>
                      )}
                      {worstHouse && bestHouse && bestHouse.name !== worstHouse.name && (
                        <p>⚠️ <strong>Pior casa:</strong> {worstHouse.name} ({formatCurrency(worstHouse.profit)})</p>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detailed Statistics - Mobile Optimized */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700">
        <div className="p-3 sm:p-4 md:p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Estatísticas Detalhadas</h3>
        </div>
        <div className="p-3 sm:p-4 md:p-6">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
            <div>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Total de Apostas</p>
              <p className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white">{filteredStats.totalBets}</p>
            </div>
            <div>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Apostas Vencedoras</p>
              <p className="text-lg sm:text-xl md:text-2xl font-bold text-green-600 dark:text-green-400">{filteredStats.greenBets}</p>
            </div>
            <div>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Apostas Perdedoras</p>
              <p className="text-lg sm:text-xl md:text-2xl font-bold text-red-600 dark:text-red-400">{filteredStats.redBets}</p>
            </div>
            <div>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Unidades Ganhas</p>
              <p className="text-lg sm:text-xl md:text-2xl font-bold text-green-600 dark:text-green-400">+{(filteredStats.totalProfit / getUserUnitValue()).toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Unidades Perdidas</p>
              <p className="text-lg sm:text-xl md:text-2xl font-bold text-red-600 dark:text-red-400">{filteredStats.totalProfit < 0 ? (filteredStats.totalProfit / getUserUnitValue()).toFixed(2) : '-0.00'}</p>
            </div>
            <div>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Saldo de Unidades</p>
              <p className={`text-lg sm:text-xl md:text-2xl font-bold ${filteredStats.totalProfit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {filteredStats.totalProfit >= 0 ? '+' : ''}{(filteredStats.totalProfit / getUserUnitValue()).toFixed(2)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Group Performance Analysis - Mobile Optimized */}
      {filteredStats.totalBets > 0 && groups.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="p-3 sm:p-4 md:p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">📊 Performance por Grupo</h3>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">Análise de lucro e desempenho de cada grupo</p>
          </div>
          <div className="p-3 sm:p-4 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {groups.map(group => {
                const groupBets = filteredBets.filter(bet => bet.group_id === group.id && bet.status !== 'pending');
                const totalBets = groupBets.length;
                const greenBets = groupBets.filter(bet => bet.result === 'green').length;
                const totalProfit = groupBets.reduce((sum, bet) => sum + (bet.profit || 0), 0);
                const totalVolume = groupBets.reduce((sum, bet) => sum + (bet.stake || 0), 0);
                const winRate = totalBets > 0 ? (greenBets / totalBets) * 100 : 0;
                const roi = totalVolume > 0 ? (totalProfit / totalVolume) * 100 : 0;
                
                if (totalBets === 0) return null;

                return (
                  <div key={group.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-gray-900 dark:text-white truncate">{group.name}</h4>
                      <span className="text-xs bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 px-2 py-1 rounded-full">
                        {totalBets} apostas
                      </span>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500 dark:text-gray-400">Lucro Total:</span>
                        <span className={`text-sm font-semibold ${
                          totalProfit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                        }`}>
                          {isPrivacyMode ? 'R$ •••' : formatCurrency(totalProfit)}
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500 dark:text-gray-400">Taxa de Acerto:</span>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                          {formatPercentage(winRate)}
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500 dark:text-gray-400">ROI:</span>
                        <span className={`text-sm font-semibold ${
                          roi >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                        }`}>
                          {formatPercentage(roi)}
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500 dark:text-gray-400">Volume:</span>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                          {isPrivacyMode ? 'R$ •••' : formatCurrency(totalVolume)}
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500 dark:text-gray-400">Unidades:</span>
                        <span className={`text-sm font-semibold ${
                          totalProfit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                        }`}>
                          {totalProfit >= 0 ? '+' : ''}{(totalProfit / getUserUnitValue()).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* No groups with bets */}
            {!groups.some(group => filteredBets.filter(bet => bet.group_id === group.id && bet.status !== 'pending').length > 0) && (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400">
                  Nenhum grupo possui apostas finalizadas no período selecionado.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Detailed Bets List - Mobile Optimized */}
      {filteredStats.totalBets > 0 && (
        <div id="bets-list-section" className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="p-3 sm:p-4 md:p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
              Lista de Apostas ({filteredStats.totalBets} encontradas)
            </h3>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
              Mostrando {startIndex + 1} a {Math.min(endIndex, filteredBets.length)} de {filteredBets.length}
            </p>
          </div>
          <div className="p-3 sm:p-4 md:p-6">
            <div className="space-y-3 sm:space-y-4">
              {paginatedBets.map((bet, index) => (
                <div key={bet.id || index} className={`p-3 sm:p-4 rounded-lg border-l-4 ${
                  bet.status === 'pending' ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/10' :
                  bet.result === 'green' ? 'border-green-500 bg-green-50 dark:bg-green-900/10' :
                  bet.result === 'red' ? 'border-red-500 bg-red-50 dark:bg-red-900/10' :
                  bet.result === 'void' ? 'border-gray-500 bg-gray-50 dark:bg-gray-900/10' :
                  'border-blue-500 bg-blue-50 dark:bg-blue-900/10'
                }`}>
                  <div className="flex flex-col sm:flex-row items-start justify-between gap-3">
                    <div className="flex-1 w-full">
                      {/* Header com resultado, tipo e data */}
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          bet.status === 'pending' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400' :
                          bet.result === 'green' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                          bet.result === 'red' ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' :
                          bet.result === 'void' ? 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400' :
                          'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                        }`}>
                          {bet.status === 'pending' ? 'Pendente' :
                           bet.result === 'green' ? 'Green' : 
                           bet.result === 'red' ? 'Red' : 
                           bet.result === 'void' ? 'Void' : 'Cashout'}
                        </span>
                        <span className="px-2 py-1 rounded-full text-xs bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300">
                          {getBetType(bet)}
                        </span>
                        <span className="text-xs text-gray-500 break-words">
                          {new Date(bet.date).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}{' '}
                          <span className="hidden sm:inline">às </span>
                          {new Date(bet.date).toLocaleTimeString('pt-BR', { 
                            hour: '2-digit', 
                            minute: '2-digit',
                            timeZone: 'UTC'
                          })}
                        </span>
                      </div>
                      
                      {/* Informações principais */}
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4 mb-2 sm:mb-3">
                        <div>
                          <p className="text-xs text-gray-500">Odd</p>
                          <p className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">{bet.odd}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Stake</p>
                          <p className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
                            {isPrivacyMode ? 'R$ •••' : formatCurrency(bet.stake)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Casa</p>
                          <p className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white truncate">
                            {getBettingHouseName(bet.betting_house_id)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Grupo</p>
                          <p className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white truncate">
                            {getGroupName(bet.group_id)}
                          </p>
                        </div>
                      </div>
                      
                      {bet.comments && (
                        <div className="bg-gray-100 dark:bg-gray-700/50 rounded p-2 text-xs text-gray-600 dark:text-gray-400 break-words">
                          <span className="font-medium">Comentários:</span> {bet.comments}
                        </div>
                      )}
                    </div>
                    
                    <div className="text-left sm:text-right w-full sm:w-auto sm:ml-4">
                      <p className="text-xs text-gray-500 mb-1">
                        {bet.status === 'pending' ? 'Aguardando' :
                         bet.result === 'cashout' ? 'Encerrado' : 'Lucro'}
                      </p>
                      <p className={`text-lg sm:text-xl font-bold ${
                        bet.status === 'pending' ? 'text-orange-600 dark:text-orange-400' :
                        bet.result === 'green' ? 'text-green-600 dark:text-green-400' :
                        bet.result === 'red' ? 'text-red-600 dark:text-red-400' :
                        bet.result === 'void' ? 'text-gray-600 dark:text-gray-400' :
                        'text-blue-600 dark:text-blue-400'
                      }`}>
                        {bet.status === 'pending' ? '-' : 
                         (isPrivacyMode ? 'R$ •••••' : formatCurrency(bet.profit || 0))}
                      </p>
                      {bet.result === 'cashout' && bet.cashout_value && (
                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                          Cashout: {isPrivacyMode ? 'R$ •••' : formatCurrency(bet.cashout_value)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Controles de Paginação */}
            {filteredBets.length > ITEMS_PER_PAGE && (
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Mostrando <span className="font-semibold">{startIndex + 1}</span> a{' '}
                  <span className="font-semibold">{Math.min(endIndex, filteredBets.length)}</span> de{' '}
                  <span className="font-semibold">{filteredBets.length}</span> apostas
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    ← Anterior
                  </button>

                  <div className="flex items-center gap-1">
                    {[...Array(totalPages)].map((_, index) => {
                      const page = index + 1;
                      // Mostrar apenas algumas páginas (primeira, última, atual e adjacentes)
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => goToPage(page)}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              currentPage === page
                                ? 'bg-blue-600 text-white'
                                : 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === currentPage - 2 || page === currentPage + 2) {
                        return <span key={page} className="px-2 text-gray-500">...</span>;
                      }
                      return null;
                    })}
                  </div>

                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Próxima →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* No Bets Found */}
      {filteredStats.totalBets === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-12">
          <div className="text-center">
            <PieChart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Nenhuma aposta encontrada
            </h3>
            <p className="text-gray-500">
              Não há apostas registradas para o período selecionado.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserReports;